import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap"
import Select from "react-select"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { projetsAPI, clientsAPI, usersAPI, teamsAPI } from "../../services/api"
import { Link } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import AlertModal from "../../components/Common/AlertModal"

const ProjetList = (props) => {
    document.title = "Liste des Projets | Ticket Management"

    const { user } = useAuth()
    const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager'
    const isClient = user?.role === 'client'

    const [projets, setProjets] = useState([])
    const [clients, setClients] = useState([])
    const [managers, setManagers] = useState([])
    const [teams, setTeams] = useState([])
    const [modal, setModal] = useState(false)
    const [editingProjet, setEditingProjet] = useState(null)

    // Modal Documents pour les clients
    const [docModal, setDocModal] = useState(false)
    const [selectedProjet, setSelectedProjet] = useState(null)
    const [documents, setDocuments] = useState([])
    const [uploading, setUploading] = useState(false)
    const [docType, setDocType] = useState("autre")

    const [formData, setFormData] = useState({
        nom: "",
        type: "externe",
        description: "",
        client_id: "",
        manager_id: "",
        team_ids: [],
        github_links: "",
        date_debut: "",
        date_fin_prevue: "",
        statut: "en_attente",
    })

    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    })

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen })

    const breadcrumbItems = [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Projets", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Liste des Projets", breadcrumbItems)
        fetchData()
    }, [])

    const fetchData = async () => {
        try {
            const calls = [projetsAPI.getAll()]

            // Un client n'a pas besoin de la liste complète des clients et managers pour son affichage
            if (isAdminOrManager) {
                calls.push(clientsAPI.getAll())
                calls.push(usersAPI.getAll({ role: 'manager' }))
                calls.push(teamsAPI.getAll())
            }

            const results = await Promise.all(calls)

            setProjets(results[0].data)
            if (isAdminOrManager) {
                setClients(results[1].data)
                setManagers(results[2].data)
                setTeams(results[3].data)
            }
        } catch (error) {
            console.error("Erreur lors du chargement des données", error)
        }
    }

    const toggleModal = () => {
        setModal(!modal)
        if (!modal) {
            setEditingProjet(null)
            setFormData({
                nom: "",
                type: "externe",
                description: "",
                client_id: "",
                manager_id: "",
                team_ids: [],
                github_links: "",
                date_debut: "",
                date_fin_prevue: "",
                statut: "en_attente",
            })
        }
    }

    const handleEdit = (projet) => {
        setEditingProjet(projet)
        setFormData({
            nom: projet.nom,
            type: projet.type,
            description: projet.description || "",
            client_id: projet.client_id || "",
            manager_id: projet.manager_id || "",
            team_ids: projet.teams ? projet.teams.map(t => t.id) : [],
            github_links: projet.github_links || "",
            date_debut: projet.date_debut ? projet.date_debut.substring(0, 10) : "",
            date_fin_prevue: projet.date_fin_prevue ? projet.date_fin_prevue.substring(0, 10) : "",
            statut: projet.statut,
        })
        setModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) {
            try {
                await projetsAPI.delete(id)
                fetchData()
            } catch (error) {
                console.error("Erreur lors de la suppression", error)
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            if (editingProjet) {
                await projetsAPI.update(editingProjet.id, formData)
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Mis à jour',
                    message: 'Le projet a été mis à jour avec succès.'
                })
            } else {
                await projetsAPI.create(formData)
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Créé',
                    message: 'Le projet a été créé avec succès.'
                })
            }
            toggleModal()
            fetchData()
        } catch (error) {
            console.error("Erreur lors de l'enregistrement", error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement du projet."
            })
        }
    }

    const toggleDocModal = (projet = null) => {
        setSelectedProjet(projet)
        setDocModal(!docModal)
        if (projet) {
            fetchDocuments(projet.id)
        }
    }

    const fetchDocuments = async (projetId) => {
        try {
            const res = await projetsAPI.getDocuments(projetId)
            setDocuments(res.data)
        } catch (error) {
            console.error("Erreur docs", error)
        }
    }

    const handleFileUpload = async (e) => {
        const file = e.target.files[0]
        if (!file || !selectedProjet) return

        const formData = new FormData()
        formData.append('fichier', file)
        formData.append('type', docType)
        formData.append('nom', file.name)

        setUploading(true)
        try {
            await projetsAPI.uploadDocument(selectedProjet.id, formData)
            fetchDocuments(selectedProjet.id)
            // Reset le champ file si possible ou juste l'état
        } catch (error) {
            console.error("Erreur upload", error)
        } finally {
            setUploading(false)
        }
    }

    const handleDeleteDoc = async (docId) => {
        if (window.confirm("Supprimer ce document ?")) {
            try {
                await projetsAPI.deleteDocument(selectedProjet.id, docId)
                fetchDocuments(selectedProjet.id)
            } catch (error) {
                console.error("Erreur delete doc", error)
            }
        }
    }

    const data = {
        columns: [
            { label: "Nom", field: "nom", sort: "asc" },
            { label: "Type", field: "type", sort: "asc" },
            { label: "Client", field: "client", sort: "asc" },
            { label: "Manager", field: "manager", sort: "asc" },
            { label: "Équipes", field: "teams_list", sort: "asc" },
            { label: "Début", field: "date_debut", sort: "asc" },
            { label: "Échéance", field: "date_fin_prevue", sort: "asc" },
            { label: "Statut", field: "statut", sort: "asc" },
            { label: "Avancement", field: "progress", sort: "asc" },
            ...(isAdminOrManager || isClient ? [{ label: "Actions", field: "actions", sort: "disabled" }] : []),
        ],
        rows: projets.map((p) => ({
            nom: isClient ? p.nom : <Link to={`/projets/${p.id}`}>{p.nom}</Link>,
            type: p.type === 'interne' ? 'Interne (Rakops)' : 'Externe',
            client: p.client ? p.client.nom : '-',
            manager: p.manager ? p.manager.name : '-',
            teams_list: p.teams && p.teams.length > 0 ? p.teams.map(t => t.nom).join(', ') : '-',
            date_debut: p.date_debut ? new Date(p.date_debut).toLocaleDateString() : '-',
            date_fin_prevue: p.date_fin_prevue ? new Date(p.date_fin_prevue).toLocaleDateString() : '-',
            statut: p.statut.replace('_', ' '),
            progress: `${p.avancement_realise || 0}%`,
            actions: (
                <div className="d-flex gap-2">
                    {isAdminOrManager && (
                        <>
                            <Button color="primary" size="sm" onClick={() => handleEdit(p)}>
                                <i className="mdi mdi-pencil"></i>
                            </Button>
                            <Button color="danger" size="sm" onClick={() => handleDelete(p.id)}>
                                <i className="mdi mdi-trash-can"></i>
                            </Button>
                        </>
                    )}
                    {isClient && (
                        <Button color="info" size="sm" onClick={() => toggleDocModal(p)}>
                            <i className="mdi mdi-file-document-outline me-1"></i> Documents
                        </Button>
                    )}
                </div>
            ),
        })),
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle className="h4">Liste des Projets</CardTitle>
                                {isAdminOrManager && (
                                    <Button color="success" onClick={toggleModal}>
                                        <i className="mdi mdi-plus me-1"></i> Nouveau Projet
                                    </Button>
                                )}
                            </div>

                            <MDBDataTable
                                noBottomColumns
                                responsive
                                striped
                                bordered
                                data={data}
                                entriesLabel="Afficher"
                                searchLabel="Rechercher"
                                infoLabel={["Affichage de", "à", "sur", "entrées"]}
                                paginationLabel={["Précédent", "Suivant"]}
                                noRecordsFoundLabel="Aucun enregistrement trouvé"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {editingProjet ? "Modifier le Projet" : "Nouveau Projet"}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="nom">Nom du Projet</Label>
                                    <Input
                                        type="text"
                                        id="nom"
                                        value={formData.nom}
                                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="type">Type de Projet</Label>
                                    <Input
                                        type="select"
                                        id="type"
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        <option value="interne">Interne (Rakops)</option>
                                        <option value="externe">Externe</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                type="textarea"
                                id="description"
                                rows="3"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </FormGroup>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="client_id">Client</Label>
                                    <Input
                                        type="select"
                                        id="client_id"
                                        value={formData.client_id}
                                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                        disabled={formData.type === 'interne'}
                                    >
                                        <option value="">Sélectionner un client</option>
                                        {clients.map((c) => (
                                            <option key={c.id} value={c.id}>{c.nom}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="manager_id">Manager</Label>
                                    <Input
                                        type="select"
                                        id="manager_id"
                                        value={formData.manager_id}
                                        onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                                    >
                                        <option value="">Sélectionner un manager</option>
                                        {managers.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label for="team_ids">Équipes affectées</Label>
                            <Select
                                isMulti
                                options={teams.map(t => ({ value: t.id, label: t.nom }))}
                                value={teams.filter(t => formData.team_ids.includes(t.id)).map(t => ({ value: t.id, label: t.nom }))}
                                onChange={(selectedOptions) => setFormData({ ...formData, team_ids: selectedOptions ? selectedOptions.map(o => o.value) : [] })}
                                classNamePrefix="select2-selection"
                                placeholder="Sélectionner des équipes..."
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="github_links">Liens Github (séparés par des virgules)</Label>
                            <Input
                                type="text"
                                id="github_links"
                                value={formData.github_links}
                                onChange={(e) => setFormData({ ...formData, github_links: e.target.value })}
                            />
                        </FormGroup>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="date_debut">Date de Début</Label>
                                    <Input
                                        type="date"
                                        id="date_debut"
                                        value={formData.date_debut}
                                        onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="date_fin_prevue">Échéance prévue</Label>
                                    <Input
                                        type="date"
                                        id="date_fin_prevue"
                                        value={formData.date_fin_prevue}
                                        onChange={(e) => setFormData({ ...formData, date_fin_prevue: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="statut">Statut</Label>
                                    <Input
                                        type="select"
                                        id="statut"
                                        value={formData.statut}
                                        onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                                    >
                                        <option value="en_attente">En attente</option>
                                        <option value="en_cours">En cours</option>
                                        <option value="termine">Terminé</option>
                                        <option value="suspendu">Suspendu</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>Annuler</Button>
                        <Button color="primary" type="submit">Enregistrer</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal Documents pour Client */}
            <Modal isOpen={docModal} toggle={() => toggleDocModal()} size="lg">
                <ModalHeader toggle={() => toggleDocModal()}>
                    Documents du projet : {selectedProjet?.nom}
                </ModalHeader>
                <ModalBody>
                    <div className="mb-4 p-3 bg-light rounded">
                        <h6>Uploader un nouveau document</h6>
                        <Row className="g-3">
                            <Col md={6}>
                                <Label for="docType">Type de document</Label>
                                <Input
                                    type="select"
                                    id="docType"
                                    value={docType}
                                    onChange={(e) => setDocType(e.target.value)}
                                >
                                    <option value="cahier_charges">Cahier des charges (CDC)</option>
                                    <option value="contrat_client">Contrat</option>
                                    <option value="specification_fonctionnelle">Spécifications techniques</option>
                                    <option value="autre">Autre document</option>
                                </Input>
                            </Col>
                            <Col md={6}>
                                <Label for="fileInput">Sélectionner le fichier</Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Input type="file" id="fileInput" onChange={handleFileUpload} disabled={uploading} />
                                    {uploading && <div className="spinner-border spinner-border-sm text-primary"></div>}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <hr />

                    <h6>Liste des documents</h6>
                    <div className="table-responsive">
                        <table className="table table-centered">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Mime Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.length > 0 ? documents.map(doc => (
                                    <tr key={doc.id}>
                                        <td>{doc.nom}</td>
                                        <td>{doc.mime_type}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-light text-success">
                                                    <i className="mdi mdi-download"></i>
                                                </a>
                                                <Button color="light" size="sm" className="text-danger" onClick={() => handleDeleteDoc(doc.id)}>
                                                    <i className="mdi mdi-delete"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="3" className="text-center">Aucun document trouvé</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggleDocModal()}>Fermer</Button>
                </ModalFooter>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment >
    )
}

export default connect(null, { setBreadcrumbItems })(ProjetList)
